import { useState } from "react";
import LazyMultiSelect from "../InputComponents/Filters/LazyMultiSelect";
import MultiSelect, { Option } from "../InputComponents/Filters/MultiSelect";
import { useMsal } from "@azure/msal-react";
import { AdminApi } from "../../Api/AdminApi";

// Define role options for the MultiSelect component
const ROLE_OPTIONS: Option[] = [
  { value: "1", label: "Encoder" },
  { value: "2", label: "Validator" },
  { value: "3", label: "Project Verificator" },
  { value: "4", label: "Project Approver" },
  // Add more role options as needed
];

export interface ProjectUserFilters {
  roles: number[];      // Using numbers for role filtering
  projects: string[];   // Project names
  users: string[];      // User names
}

interface ProjectUserFilterProps {
  onFiltersChange: (filters: ProjectUserFilters) => void;
}

const ProjectUserFilter: React.FC<ProjectUserFilterProps> = ({ onFiltersChange }) => {
  const { instance, accounts } = useMsal();
  const api = new AdminApi(accounts[0], instance);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Custom function to handle role selection
  const handleRoleSelectionChange = (values: string[]) => {
    // Convert string values to numbers
    const roleNumbers = values.map(value => parseInt(value, 10));
    
    setSelectedRoles(roleNumbers);
    onFiltersChange({
      roles: roleNumbers,
      projects: selectedProjects,
      users: selectedUsers
    });
  };

  const handleProjectSelectionChange = (values: string[]) => {
    setSelectedProjects(values);
    onFiltersChange({
      roles: selectedRoles,
      projects: values,
      users: selectedUsers
    });
  };

  const handleUserSelectionChange = (values: string[]) => {
    setSelectedUsers(values);
    onFiltersChange({
      roles: selectedRoles,
      projects: selectedProjects,
      users: values
    });
  };
  
  const injectCss = `
    .custom-form-control,
    .custom-form-select,
    .custom-form-select option {
      height: 60px !important;
      font-size: 19px !important;
      font-family: inherit !important;
    }
  `;

  const buttonStyle = {
    fontSize: "14px",
    fontWeight: 400,
    padding: "4px 8px",
    color: "#6c757d",
    textDecoration: "none",
    border: "none",
    background: "none"
  };

  return (
    <div className="mt-4 mb-4">
      <style>{injectCss}</style>
      
      <div className="d-flex justify-content-end mb-2">
        <button 
          onClick={() => setFiltersVisible(!filtersVisible)}
          className="btn btn-sm text-secondary"
          style={buttonStyle}
        >
          {filtersVisible ? "Hide filters" : "Show filters"}
        </button>
      </div>
      {filtersVisible && (
        <div className="row g-2 mb-2">
          <div className="col-md-4">
            <MultiSelect
              onSelectionChange={handleRoleSelectionChange}
              placeholder="Filter by Role"
              className="w-100" 
              options={ROLE_OPTIONS}
              initialValue={selectedRoles.map(role => role.toString())}
            />
          </div>
          <div className="col-md-4">
            <LazyMultiSelect
              onSelectionChange={handleProjectSelectionChange}
              placeholder="Filter by Project"
              className="w-100" 
              fetchFunction={(query, page, limit) => api.getProjectNames(query, page, limit)}
              initialValue={selectedProjects}
            />
          </div>
          <div className="col-md-4">
            <LazyMultiSelect
              onSelectionChange={handleUserSelectionChange}
              placeholder="Filter by User"
              className="w-100" 
              fetchFunction={(query, page, limit) => api.getUserNames(query, page, limit)}
              initialValue={selectedUsers}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectUserFilter;